import { db, LocalRoutine } from '../db/index.js';
import {
  getRoutinesToPush,
  getRoutineTombstones,
  hardDeleteLocalRoutine,
  markRoutinePushed,
  markRoutinePushRejected
} from '../db/operations.js';
import { apiClient, ApiError } from '../utils/apiClient.js';
import { useAuthStore } from '../stores/authStore.js';
import { isUuid } from '../utils/id.js';
import type { ApiRoutine, RoutineBlock, RoutineUpsertBody } from '../types/index.js';

export const ROUTINES_CHANGED_EVENT = 'fretboard:routines-changed';
export const ROUTINE_RESTORED_EVENT = 'fretboard:routine-restored';
export const ROUTINE_SYNC_REJECTED_EVENT = 'fretboard:routine-sync-rejected';

/** Server user id used to filter local routines; guests and logged-out users get undefined. */
export const getCurrentUserId = (): string | undefined => {
  const { user, isGuest, token } = useAuthStore.getState();
  return isGuest || !token ? undefined : user?.id;
};

let inFlight: Promise<void> | null = null;
let rerunRequested = false;

const clampInt = (n: unknown, min: number, max: number, fallback: number): number => {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v)) return fallback;
  return Math.min(max, Math.max(min, v));
};

/** Builds the PUT body, clamped into the server's bounds. Never sends `tab` (it can hold a localBlob). */
export function toUpsertBody(r: LocalRoutine): RoutineUpsertBody {
  const title = (r.title ?? '').trim().slice(0, 120) || 'روتین';
  return {
    title,
    target_duration_minutes: clampInt(r.targetDurationMinutes, 1, 600, 1),
    replaces_legacy: r.legacy === 1,
    blocks: r.blocks.map((b) => {
      let start = b.startMeasure == null ? null : clampInt(b.startMeasure, 1, 10000, 1);
      let end = b.endMeasure == null ? null : clampInt(b.endMeasure, 1, 10000, 1);
      if (start != null && end != null && start > end) [start, end] = [end, start];
      const bpm = b.targetBpm == null || !Number.isFinite(Number(b.targetBpm)) || Number(b.targetBpm) <= 0
        ? null
        : clampInt(b.targetBpm, 20, 400, 20);
      return {
        id: b.id,
        category: b.category,
        duration_minutes: clampInt(b.durationMinutes, 1, 240, 1),
        tab_id: isUuid(b.tabId) ? b.tabId : null,
        start_measure: start,
        end_measure: end,
        target_bpm: bpm,
        notes: b.notes ? b.notes.slice(0, 2000) : null,
        // Always sent (TL-04/TL-06): explicit null clears, the exercise payload goes back verbatim.
        title: b.title ? b.title.trim().slice(0, 80) || null : null,
        exercise: b.exercise ?? null
      };
    })
  };
}

/** Maps a server routine to a clean local record owned by `userId`. */
export function fromServer(s: ApiRoutine, userId: string): LocalRoutine {
  return {
    id: s.id,
    title: s.title,
    targetDurationMinutes: s.targetDurationMinutes,
    createdAt: s.createdAt,
    updatedAt: s.updatedAt,
    blocks: (s.blocks ?? []).map(
      (b): RoutineBlock => ({
        id: b.id,
        routineId: s.id,
        category: b.category,
        durationMinutes: b.durationMinutes,
        tabId: b.tabId,
        startMeasure: b.startMeasure,
        endMeasure: b.endMeasure,
        targetBpm: b.targetBpm,
        notes: b.notes ?? undefined,
        tab: b.tab ?? undefined,
        title: b.title ?? null,
        exercise: b.exercise ?? null
      })
    ),
    dirty: 0,
    ownerId: userId,
    serverUpdatedAt: s.updatedAt,
    legacy: 0
  };
}

/**
 * FRE-142 D9: a server copy has `tabId: null` for a block whose tab the server does not know yet (still queued
 * for upload). Keeps the local link for blocks (matched by block id) that point at such a pending tab.
 * Returns `preserved: true` when at least one link was kept (the routine must then be pushed again).
 */
export function preservePendingTabLinks(
  server: LocalRoutine,
  local: LocalRoutine,
  pendingTabIds: ReadonlySet<string>
): { routine: LocalRoutine; preserved: boolean } {
  let preserved = false;
  const localByBlock = new Map(local.blocks.map((b) => [b.id, b]));
  const blocks = server.blocks.map((b) => {
    if (b.tabId) return b;
    const lb = localByBlock.get(b.id);
    if (lb?.tabId && pendingTabIds.has(lb.tabId)) {
      preserved = true;
      return { ...b, tabId: lb.tabId, tab: lb.tab ?? b.tab };
    }
    // FRE-48: a non-UUID tab id is device-only and can never be pushed (toUpsertBody sends null), so the server
    // will always say null. Keep the local link; no re-push needed (it would be a no-op loop).
    if (lb?.tabId && !isUuid(lb.tabId)) {
      return { ...b, tabId: lb.tabId, tab: lb.tab ?? b.tab };
    }
    return b;
  });
  return { routine: { ...server, blocks }, preserved };
}

const statusOf = (err: unknown): number | undefined => (err instanceof ApiError ? err.status : undefined);

async function pushDeletes(userId: string): Promise<void> {
  const tombstones = await getRoutineTombstones(userId);
  for (const r of tombstones) {
    try {
      await apiClient.delete(`/api/routines/${r.id}`);
      await hardDeleteLocalRoutine(r.id);
    } catch (err) {
      if (statusOf(err) === 404) {
        await hardDeleteLocalRoutine(r.id);
        continue;
      }
      throw err;
    }
  }
}

async function pushUpserts(userId: string): Promise<void> {
  const dirty = await getRoutinesToPush(userId);
  for (const r of dirty) {
    if (!isUuid(r.id) || r.blocks.some((b) => !isUuid(b.id))) {
      console.warn('routineSync: skipping routine with non-UUID id', r.id);
      continue;
    }
    const snapshot = r.updatedAt;
    try {
      const res = await apiClient.put<ApiRoutine & { restored?: boolean }>(`/api/routines/${r.id}`, toUpsertBody(r));
      if (res.restored === true) {
        window.dispatchEvent(new CustomEvent(ROUTINE_RESTORED_EVENT, { detail: { title: r.title } }));
      }
      const outcome = await markRoutinePushed(r.id, snapshot, res.updatedAt, userId);
      if (outcome === 'gone') {
        // Deleted locally while the PUT was in flight: remove it from the server too.
        try {
          await apiClient.delete(`/api/routines/${r.id}`);
        } catch (err) {
          if (statusOf(err) !== 404) throw err;
        }
      }
    } catch (err) {
      const status = statusOf(err);
      if (status === 400 || status === 409) {
        console.warn('routineSync: server rejected routine', r.id, status);
        const marked = await markRoutinePushRejected(r.id, snapshot, status);
        if (marked) {
          window.dispatchEvent(
            new CustomEvent(ROUTINE_SYNC_REJECTED_EVENT, { detail: { id: r.id, title: r.title, status, updatedAt: snapshot } })
          );
        }
        continue;
      }
      throw err;
    }
  }
}

async function pullRoutines(userId: string): Promise<void> {
  // Snapshot of clean, synced local routines before the request, so a push from another tab
  // that lands between the GET and the transaction is never mistaken for a remote delete.
  const before = await db.routines.toArray();
  const removalCandidates = new Map<string, string>();
  for (const l of before) {
    if (l.ownerId === userId && l.dirty === 0 && !l.deletedAt && l.serverUpdatedAt) {
      removalCandidates.set(l.id, l.serverUpdatedAt);
    }
  }

  // A failed GET throws and aborts the run: an error is never treated as an empty list.
  const server = await apiClient.get<ApiRoutine[]>('/api/routines');
  if (!Array.isArray(server)) return;
  const serverIds = new Set(server.map((s) => s.id));

  let changed = false;
  await db.transaction('rw', [db.routines, db.tabs], async () => {
    const pendingTabIds = new Set<string>(
      (await db.tabs.where('pendingUpload').equals(1).toArray()).map((t) => t.id)
    );
    for (const s of server) {
      const local = await db.routines.get(s.id);
      if (!local) {
        await db.routines.put(fromServer(s, userId));
        changed = true;
        const title = s.title.trim();
        const samples = await db.routines.filter((x) => x.isSample === 1 && x.dirty === 0 && !x.serverUpdatedAt && x.title.trim() === title).toArray();
        for (const sample of samples) {
          await db.routines.delete(sample.id);
        }
        continue;
      }
      if (local.deletedAt || local.dirty === 1) continue;
      if (local.ownerId && local.ownerId !== userId) continue;
      if (local.serverUpdatedAt !== s.updatedAt) {
        const { routine, preserved } = preservePendingTabLinks(fromServer(s, userId), local, pendingTabIds);
        await db.routines.put({
          ...routine,
          isSample: local.isSample,
          ...(preserved && { dirty: 1 as const, updatedAt: new Date().toISOString() })
        });
        changed = true;
      }
    }

    for (const [id, snapshotUpdatedAt] of removalCandidates) {
      if (serverIds.has(id)) continue;
      const cur = await db.routines.get(id);
      if (cur && cur.dirty === 0 && !cur.deletedAt && cur.ownerId === userId && cur.serverUpdatedAt === snapshotUpdatedAt) {
        await db.routines.delete(id);
        changed = true;
      }
    }
  });

  if (changed) {
    window.dispatchEvent(new CustomEvent(ROUTINES_CHANGED_EVENT, { detail: { reason: 'pull' } }));
  }
}

async function runOnce(): Promise<void> {
  const { token, isGuest, user } = useAuthStore.getState();
  if (!token || isGuest || !user) return;
  const userId = user.id;

  await pushDeletes(userId);
  await pushUpserts(userId);
  await pullRoutines(userId);
}

/**
 * Routine sync: push tombstones, push dirty routines, then pull. Single in-flight run per tab;
 * a call during a run schedules exactly one more run afterwards. Guests are a no-op.
 * triggerSync awaits this before pushing practice sessions, so the block ids that session
 * logs reference already exist on the server.
 */
export function syncRoutines(): Promise<void> {
  if (inFlight) {
    rerunRequested = true;
    return inFlight;
  }
  inFlight = (async () => {
    try {
      do {
        rerunRequested = false;
        await runOnce();
      } while (rerunRequested);
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/** Fire and forget; no-op offline; errors are swallowed. */
export function requestRoutineSync(): void {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  syncRoutines().catch((err) => {
    console.warn('routineSync: background sync failed', err);
  });
}
